/**
 * WebSocket Notifications Service
 * Gère les connexions WebSocket pour les notifications temps réel
 * Avec alertes sonores pour les demandes WhatsApp
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Son de notification (base64 encoded beep court)
const NOTIFICATION_SOUND_URL = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleVU4Y5mcVRUAEWmf1NKtaTghN2+qzsetcUc2SmJiQC4XL1iPtM6qaUMqNWCix8+9kl04JUVXWz4xLzxYjbTIonNMNEBahrHMvZNeOy1ESzckNVNfgaXFpnVSOkBfdpy9sn1UPjxGKwIQOm6YsbCOY0s8TXqnxb+JVjkxLgwAFF+Qr8a0hFo9PFd0nLnAdE0zLh4AAT59qsu7fE43OFN6p8O8gE4yKhIADmecvMamd0gqLlyCr8u4cD0iFhAAGHKrz8Scc0AhJFN8q8i2bzoeDQAALIOzz7t9UD43SWmVucqvXysSDgACUYy1zbBxSTU6YYSqw7tuPB8MAAxjnMPFm2A3KUBpkLbFqmQ5GgUABF6Wvsqna0kqLkpzmLnGo107HQcABWGZv8ygYT0lK0pzl7TDo1w5FgQAB2SbwMydXjsjKkp1mrfDo1w4FQQAB2aewsybWzgiKEp3nbnEpF46FgUAB2efw82bWjcgJ0l4n7vGpmA7GQcABmifwcyaWTYfJkh4oL3HqGI9GwkABmigw82bWTYfJUh5ob/Jq2VAHQ0ABWqhxM6cWjYfJEh7o8HLrmhEIBIAA2ujxs+fXTggJUh9psTOs21JJBYAA2ylx9CgXjkgJEiAp8jRtHFNKRsAAm+nyNKiYDsiJUmCqsvUuHZRLh8AAnCpytOkYz0jJkqErM3WvHpWMyQAAXKqzNWnZT8kJ0uFrs/Yv35aOCgAAHOszdepZ0AmKE2HsdHbwoJdPCwAAHOtztiqa0IoKk+Js9Pdw4ZgPzAAAHSu0Nqsb0UpK1GLtdXfxolgQjMAAHWv0dyucUcrLFONuNbhyYtiRDYAAHaw0t2wdEkuLlWPutnj0I9mRzkAAHex0962dkswMFiRvNvk0pJpSjwAAHiz1N+4eE0yMlqTvt3m1JVrTD8AAHm01eC7e08zM1yVv97n1phuT0EAAHq11uG9fVE1NV6Xwd/p2Jtx';

class NotificationService {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.pingInterval = null;
    this.soundEnabled = true;
    this.audio = null;
    this.initAudio();
  }

  /**
   * Initialiser l'audio pour les alertes sonores
   */
  initAudio() {
    try {
      this.audio = new Audio(NOTIFICATION_SOUND_URL);
      this.audio.volume = 0.7;
    } catch (e) {
      console.warn('Audio initialization failed:', e);
    }
  }

  /**
   * Jouer le son de notification
   */
  playNotificationSound() {
    if (!this.soundEnabled || !this.audio) return;
    
    try {
      // Cloner l'audio pour pouvoir jouer plusieurs fois rapidement
      const sound = this.audio.cloneNode();
      sound.volume = 0.7;
      sound.play().catch(e => console.warn('Audio play failed:', e));
    } catch (e) {
      console.warn('Failed to play notification sound:', e);
    }
  }

  /**
   * Activer/désactiver le son
   */
  toggleSound(enabled) {
    this.soundEnabled = enabled;
  }

  /**
   * Se connecter au WebSocket
   */
  connect(token) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    // Construire l'URL WebSocket
    const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/notifications/ws/${token}`;

    console.log('Connecting to WebSocket...');
    
    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.startPing();
        this.emit('connected', {});
      };

      this.ws.onmessage = (event) => {
        try {
          // Handle ping/pong
          if (event.data === 'ping') {
            this.ws.send('pong');
            return;
          }
          if (event.data === 'pong') {
            return;
          }

          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this.stopPing();
        this.emit('disconnected', {});
        
        // Tentative de reconnexion automatique
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`Reconnecting... Attempt ${this.reconnectAttempts}`);
          setTimeout(() => this.connect(token), this.reconnectDelay);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.emit('error', { error });
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }

  /**
   * Se déconnecter
   */
  disconnect() {
    this.stopPing();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Démarrer le ping pour maintenir la connexion
   */
  startPing() {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send('ping');
      }
    }, 25000); // Ping toutes les 25 secondes
  }

  /**
   * Arrêter le ping
   */
  stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Demander une mise à jour des compteurs
   */
  refresh() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send('refresh');
    }
  }

  /**
   * Gérer les messages reçus
   */
  handleMessage(message) {
    const { type, data } = message;
    
    switch (type) {
      case 'initial_counts':
        this.emit('badge_update', data);
        break;
      case 'counts_update':
        // Vérifier si c'est une nouvelle demande (comparaison avec les anciens compteurs)
        this.emit('badge_update', data);
        break;
      case 'new_quote_request':
        // Jouer le son pour les nouvelles demandes WhatsApp
        this.playNotificationSound();
        this.emit('new_quote_request', data);
        if (data.counts) {
          this.emit('badge_update', data.counts);
        }
        break;
      case 'new_diagnostic':
        this.playNotificationSound();
        this.emit('new_diagnostic', data);
        if (data.counts) {
          this.emit('badge_update', data.counts);
        }
        break;
      case 'new_whatsapp_message':
        // Alerte sonore pour les messages WhatsApp
        this.playNotificationSound();
        this.emit('new_whatsapp_message', data);
        if (data.counts) {
          this.emit('badge_update', data.counts);
        }
        break;
      default:
        this.emit(type, data);
    }
  }

  /**
   * S'abonner à un événement
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    // Retourner une fonction pour se désabonner
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  /**
   * Émettre un événement
   */
  emit(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Vérifier si connecté
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Instance singleton
const notificationService = new NotificationService();

export default notificationService;
